import React, { useCallback, useState } from 'react'
import { Pencil, Trash2, Plus } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import type { PlanRecord } from '../types/index.js'
import PlanFormModal from './PlanFormModal.js'

interface Props {
  plans: PlanRecord[]
  loading: boolean
  onCreate: (input: { name: string; seats: number; monthlyAiCredits: number }) => Promise<void>
  onUpdate: (id: string, input: { name: string; seats: number; monthlyAiCredits: number }) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return iso
  }
}

const SkeletonRow: React.FC<{ border: string }> = ({ border }) => (
  <tr>
    {[40, 20, 30, 20, 15].map((w, i) => (
      <td key={i} className="px-4 py-3">
        <div className="h-4 rounded-full animate-pulse" style={{ backgroundColor: border, width: `${w}%` }} />
      </td>
    ))}
  </tr>
)

const PlanTable: React.FC<Props> = ({ plans, loading, onCreate, onUpdate, onDelete }) => {
  const { currentTheme: t } = useTheme()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingPlan, setEditingPlan] = useState<PlanRecord | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [planPendingDelete, setPlanPendingDelete] = useState<PlanRecord | null>(null)

  const openCreate = useCallback(() => {
    setEditingPlan(null)
    setModalOpen(true)
  }, [])

  const openEdit = useCallback((plan: PlanRecord) => {
    setEditingPlan(plan)
    setModalOpen(true)
  }, [])

  const handleSubmit = useCallback(
    async (input: { name: string; seats: number; monthlyAiCredits: number }) => {
      if (editingPlan) {
        await onUpdate(editingPlan.id, input)
      } else {
        await onCreate(input)
      }
    },
    [editingPlan, onCreate, onUpdate],
  )

  const handleDelete = useCallback(
    async (id: string) => {
      setDeleteError(null)
      setDeletingId(id)
      try {
        await onDelete(id)
      } catch (err) {
        setDeleteError(err instanceof Error ? err.message : 'Failed to delete plan')
      } finally {
        setDeletingId(null)
      }
    },
    [onDelete],
  )

  const confirmDelete = useCallback(() => {
    if (!planPendingDelete) return
    void handleDelete(planPendingDelete.id)
    setPlanPendingDelete(null)
  }, [planPendingDelete, handleDelete])

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm" style={{ color: t.secondrytext }}>
          {plans.length} plan{plans.length !== 1 ? 's' : ''}
        </p>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition"
          style={{ backgroundColor: t.accent, color: t.buttonText }}
        >
          <Plus size={15} />
          New Plan
        </button>
      </div>

      {deleteError && (
        <div
          className="mb-4 rounded-lg px-4 py-2.5 text-sm"
          style={{ backgroundColor: t.errorBackground, color: t.errorText, border: `1px solid ${t.border}` }}
        >
          {deleteError}
        </div>
      )}

      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: t.border }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: t.secondary }}>
                {['Name', 'Seats', 'Credits / month', 'Created', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide"
                    style={{ color: t.secondrytext }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={i} border={t.border} />)
              ) : plans.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-sm" style={{ color: t.secondrytext }}>
                    No plans yet.
                  </td>
                </tr>
              ) : (
                plans.map((plan) => (
                  <tr key={plan.id} className="border-t" style={{ borderColor: t.border }}>
                    <td className="px-4 py-3 font-medium" style={{ color: t.text }}>{plan.name}</td>
                    <td className="px-4 py-3" style={{ color: t.text }}>{plan.seats}</td>
                    <td className="px-4 py-3" style={{ color: t.text }}>{plan.monthlyAiCredits.toLocaleString()}</td>
                    <td className="px-4 py-3" style={{ color: t.secondrytext }}>{formatDate(plan.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => openEdit(plan)}
                          aria-label="Edit plan"
                          className="w-8 h-8 rounded-lg flex items-center justify-center transition"
                          style={{ color: t.secondrytext }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = t.hoverBackground }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent' }}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          disabled={deletingId === plan.id}
                          onClick={() => setPlanPendingDelete(plan)}
                          aria-label="Delete plan"
                          className="w-8 h-8 rounded-lg flex items-center justify-center transition disabled:opacity-50"
                          style={{ color: t.errorText }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = t.errorBackground }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PlanFormModal
        plan={editingPlan}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />

      <Dialog open={planPendingDelete !== null} onOpenChange={(open) => { if (!open) setPlanPendingDelete(null) }}>
        <DialogContent
          className="py-6 px-8 rounded-2xl shadow-xl max-w-sm w-full border-none"
          style={{ background: t.secondary, color: t.text }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-center" style={{ color: t.text }}>
              Delete "{planPendingDelete?.name}"?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-center mt-1" style={{ color: t.mutedText }}>
            This plan will be permanently removed. This cannot be undone.
          </p>
          <DialogFooter className="flex justify-center gap-3 mt-6 flex-row sm:justify-center">
            <Button
              variant="secondary"
              onClick={() => setPlanPendingDelete(null)}
              className="px-5 py-2 rounded-lg font-medium h-auto text-sm"
              style={{ background: t.hoverBackground, color: t.text, border: `1px solid ${t.border}` }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium h-auto text-sm"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default React.memo(PlanTable)
